import React from 'react'
import { Box, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
} from 'chart.js'
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'

ChartJS.register(ArcElement, BarElement, CategoryScale, Legend, LinearScale, LineElement, PointElement, Tooltip)

const CHARTS = { bar: Bar, line: Line, pie: Pie, doughnut: Doughnut }

// Product status types (label_kind 'enum') translate through the usual keys;
// anything else is customer data and is shown exactly as it arrives. Without a
// translation the source value stays, never a raw key or a gap.
const displayLabels = (spec, t_) => spec.label_kind === 'enum'
  ? spec.labels.map(label => {
    const translated = t_(label)
    return translated && translated !== label ? translated : label
  })
  : spec.labels

/**
 * A chart sent by the assistant (show_chart). The gateway builds it from the
 * real result of a query, so the values are drawn as they come.
 */
const AssistantChart = ({ spec }) => {
  const theme = useTheme()
  const { t_ } = useSmartTranslation()
  const ChartType = CHARTS[spec.type] || Bar
  const circular = spec.type === 'pie' || spec.type === 'doughnut'
  const series = [theme.palette.primary.main, theme.palette.info.main, theme.palette.warning.main, theme.palette.success.main, theme.palette.error.main, theme.palette.secondary.main]
  const textColor = theme.palette.text.secondary
  const gridColor = alpha(theme.palette.text.primary, 0.08)

  const data = {
    labels: displayLabels(spec, t_),
    datasets: spec.datasets.map((dataset, index) => ({
      label: dataset.label,
      data: dataset.data,
      backgroundColor: circular ? spec.labels.map((_, i) => series[i % series.length]) : alpha(series[index % series.length], 0.75),
      borderColor: circular ? theme.palette.background.paper : series[index % series.length],
      borderWidth: circular ? 2 : 1
    }))
  }
  const axis = (title) => ({
    ticks: { color: textColor },
    grid: { color: gridColor },
    title: { display: Boolean(title), text: title, color: textColor }
  })
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: circular || spec.datasets.length > 1, labels: { color: textColor } }
    },
    scales: circular ? {} : { x: axis(spec.x_label), y: { ...axis(spec.y_label), beginAtZero: true } }
  }

  return (
    <Box sx={{ mt: 1, width: '100%' }}>
      {spec.title ? <Typography variant='subtitle2' sx={{ mb: 0.5 }}>{spec.title}</Typography> : null}
      <Box sx={{ position: 'relative', height: 220 }}>
        <ChartType data={data} options={options} />
      </Box>
      {spec.sql
        ? (
          <Box component='details' sx={{ mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
            <Box component='summary' sx={{ cursor: 'pointer' }}>{t_('Show query')}</Box>
            <Box component='pre' sx={{ m: 0, mt: 0.5, p: 1, borderRadius: 1, overflowX: 'auto', whiteSpace: 'pre-wrap', bgcolor: theme => alpha(theme.palette.text.primary, 0.05) }}>{spec.sql}</Box>
          </Box>
          )
        : null}
    </Box>
  )
}

export default AssistantChart
